import { z } from 'zod';

import { HTTPError, type MaybeHTTPError } from './HTTPError';

/**
 * Sanitize an error message for a user.
 * This method should be used to render an error.
 */
export function sanitizeError(error: MaybeHTTPError, defaultMessage = 'Unexpected error'): string {
    if (isExposable(error)) {
        return error.message;
    }

    return defaultMessage;
}

/**
 * A small utility to format an error to a diagnostic log line.
 */
export function formatErrorForLog(error: unknown, maxFrames = 5): string {
    if (!(error instanceof Error)) {
        return typeof error === 'string' ? error : JSON.stringify(error);
    }

    const header = `${error.name}: ${error.message}`;

    const frames = (error.stack ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('at '))
        .slice(0, maxFrames)
        .join(' | ');

    const cause = error.cause ? ` [cause: ${formatErrorForLog(error.cause, 2)}]` : '';

    return frames ? `${header} | ${frames}${cause}` : `${header}${cause}`;
}

/**
 * Schema for errors that follow the http-errors convention (e.g. body-parser parse failures).
 * These errors set statusCode, expose, and message directly on the Error instance.
 */
const HttpErrorLikeSchema = z.object({
    message: z.string(),
    statusCode: z.number(),
    expose: z.boolean(),
});

/**
 * Schema to extract HTTPError from tRPC error data.
 */
const HTTPErrorDataSchema = z.object({
    httpError: HttpErrorLikeSchema.extend({ expose: z.boolean().optional() }),
});

/**
 * Try to extract an HTTPError from the error data, if it exists.
 * This is used to extract HTTPError that have been serialized through tRPC.
 */
function getHTTPErrorFromData(error: MaybeHTTPError): HTTPError | null {
    if (error instanceof Error && 'data' in error) {
        const parseResult = HTTPErrorDataSchema.safeParse(error.data);
        if (parseResult.success) {
            const { message, statusCode, expose = false } = parseResult.data.httpError;
            return new HTTPError(message, statusCode, expose, { cause: error });
        }
    }
    return null;
}

export function extractHTTPError(error: MaybeHTTPError): {
    statusCode: number;
    message: string;
    headers: Record<string, string>;
} {
    const errorFromData = getHTTPErrorFromData(error);
    if (errorFromData) {
        return extractHTTPError(errorFromData);
    }

    if (error instanceof HTTPError) {
        return {
            statusCode: error.statusCode ?? 500,
            message: sanitizeError(error, 'Internal Server Error'),
            headers: error.headers,
        };
    }

    // Handle errors from middleware (e.g. body-parser) that follow the http-errors convention.
    const httpErrorLike = HttpErrorLikeSchema.safeParse(error);
    if (httpErrorLike.success) {
        return {
            statusCode: httpErrorLike.data.statusCode,
            message: httpErrorLike.data.expose
                ? httpErrorLike.data.message
                : 'Internal Server Error',
            headers: {},
        };
    }

    return {
        statusCode: 500,
        message: 'Internal Server Error',
        headers: {},
    };
}

/**
 * Returns true if the error is exposable.
 * Exposable errors can be shown to a user, non-exposable errors are unexpected and often unhandled.
 */
export function isExposable(error: unknown): error is HTTPError {
    return error instanceof HTTPError && error.expose;
}

/**
 * Throws the error if it is not an exposable error.
 * Exposable errors can be shown to a user, non-exposable errors are unexpected and often unhandled.
 */
export function throwIfNotExposable(error: unknown): asserts error is HTTPError {
    if (!isExposable(error)) {
        throw error;
    }
}

/**
 * Runs promises in parallel and awaits all of them, even if some of them reject.
 * If there are any rejections, throws the most appropriate one among all.
 * Prioritizes 401, then 403, then the first error.
 *
 * Promise.all should be preferred for the sake of efficiency if there are no promises
 * that can reject with a 401 or 403 because it will exit early at the first rejection
 * and not await the other promises.
 *
 * @param promises an array of promises
 * @returns array of resolved promises if none of them are rejected
 */
export const resolveAllOrThrowBest: typeof Promise.all = async (promises: Promise<unknown>[]) => {
    const results = await Promise.allSettled(promises);

    const rejections = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);

    // if all promises are resolved, return the results as if returning from Promise.all
    if (rejections.length === 0) {
        return results.map((result) => (result as PromiseFulfilledResult<unknown>).value);
    }

    const authenticationError = rejections.find(
        (err) => err instanceof HTTPError && err.statusCode === 401
    );
    if (authenticationError !== undefined) {
        throw authenticationError;
    }

    const authorizationError = rejections.find(
        (err) => err instanceof HTTPError && err.statusCode === 403
    );
    if (authorizationError !== undefined) {
        throw authorizationError;
    }

    throw rejections[0];
};

export function assertError(error: unknown): asserts error is Error {
    if (!(error instanceof Error)) {
        throw error;
    }
}
